/**
 * DoseSpot Patients section for the Organization detail page in admin-v2.
 *
 * Allows admins to search DoseSpot patients by name and view/edit their demographics.
 */

import { useState } from 'react';
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material';
import { Search } from '@mui/icons-material';

import { DataTable } from './data-table';
import { DosespotPatientDrawer } from './dosespot-patient-drawer';
import { useSearchDosespotPatientsQuery } from '../store/api';
import type { DosespotPatientData } from '../store/api';

interface OrganizationDosespotPatientsSectionProps {
  organizationId: string;
}


export function OrganizationDosespotPatientsSection({
  organizationId,
}: OrganizationDosespotPatientsSectionProps) {
  const [firstname, setFirstname] = useState('');
  const [lastname, setLastname] = useState('');
  const [searchParams, setSearchParams] = useState<{
    firstname?: string;
    lastname?: string;
  } | null>(null);
  const [selectedPatientId, setSelectedPatientId] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const canSearch = firstname.trim().length >= 2 || lastname.trim().length >= 2;

  const { data, isLoading, error } = useSearchDosespotPatientsQuery(
    {
      organizationId,
      firstname: searchParams?.firstname,
      lastname: searchParams?.lastname,
    },
    { skip: !searchParams }
  );

  const handleSearch = () => {
    if (!canSearch) return;
    setSearchParams({
      firstname: firstname.trim() || undefined,
      lastname: lastname.trim() || undefined,
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && canSearch) {
      handleSearch();
    }
  };

  const handleRowClick = (row: DosespotPatientData) => {
    if (row.PatientId) {
      setSelectedPatientId(String(row.PatientId));
      setDrawerOpen(true);
    }
  };

  const columns = [
    {
      key: 'PatientId' as const,
      label: 'ID',
      render: (value: unknown) => (
        <span className="font-mono text-xs text-gray-500">{String(value)}</span>
      ),
    },
    {
      key: 'FirstName' as const,
      label: 'Name',
      render: (_value: unknown, row: DosespotPatientData) =>
        `${row.FirstName || ''} ${row.LastName || ''}`.trim(),
    },
    {
      key: 'DateOfBirth' as const,
      label: 'DOB',
      render: (value: unknown) => {
        if (!value) return '—';
        const date = new Date(String(value));
        return date.toLocaleDateString();
      },
    },
    {
      key: 'Gender' as const,
      label: 'Gender',
      render: (value: unknown) => String(value || '—'),
    },
    {
      key: 'State' as const,
      label: 'Location',
      render: (_value: unknown, row: DosespotPatientData) =>
        [row.City, row.State].filter(Boolean).join(', ') || '—',
    },
    {
      key: 'Active' as const,
      label: 'Status',
      render: (value: unknown) => (
        <Chip
          label={value ? 'Active' : 'Inactive'}
          size="small"
          color={value ? 'success' : 'default'}
          variant="outlined"
        />
      ),
    },
  ];

  return (
    <div>
      <div className="bg-white rounded-lg shadow p-6 mb-4">
        <Typography variant="h6" gutterBottom>
          DoseSpot Patient Search
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Search for patients in DoseSpot by name (minimum 2 characters).
        </Typography>

        <div className="flex gap-3 items-end">
          <TextField
            label="First Name"
            size="small"
            value={firstname}
            onChange={(e) => setFirstname(e.target.value)}
            onKeyDown={handleKeyDown}
            sx={{ width: 200 }}
          />
          <TextField
            label="Last Name"
            size="small"
            value={lastname}
            onChange={(e) => setLastname(e.target.value)}
            onKeyDown={handleKeyDown}
            sx={{ width: 200 }}
          />
          <Button
            variant="contained"
            startIcon={isLoading ? <CircularProgress size={16} /> : <Search />}
            onClick={handleSearch}
            disabled={!canSearch || isLoading}
          >
            Search
          </Button>
        </div>
      </div>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to search patients. The organization may not be connected to DoseSpot.
        </Alert>
      )}

      {searchParams && (
        <DataTable
          title="Search Results"
          columns={columns}
          data={data?.Items || []}
          total={data?.Items?.length}
          loading={isLoading}
          onRowClick={handleRowClick}
          emptyMessage="No patients found matching your search criteria."
        />
      )}

      {!searchParams && (
        <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
          <Typography variant="body1">
            Search for patients by name to get started.
          </Typography>
        </div>
      )}

      <DosespotPatientDrawer
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setSelectedPatientId(null);
        }}
        organizationId={organizationId}
        dosespotPatientId={selectedPatientId}
      />
    </div>
  );
}
